// TelcoTrack — hotovost stavby ve 4 kategoriích (Výkop / Montáž / Montáž optika / Ostatní) + mapování na PPD finance buckety.

import type { FinanceWorkBucketId } from './ppd-finance-categories.constants';
import {
  isFinanceStavbaObnovaLine,
  isFinanceStavbaPokladkaLine,
  isFinanceStavbaVykopLine,
  isZafukWorkName,
  FINANCE_MONTAZ_MULTIDUCT_SAP,
  FINANCE_MONTAZ_ZAVRT_SAP,
  isMontazZavrtPpdLine,
  resolveWorkReportFinanceBucket,
} from './ppd-finance-categories.constants';
import { getObjectPhaseProgressPercent } from './object-workflow/phase-progress';
import { normalizeObjectTypeForLookup } from './object-workflow/phase-codes';
import { hasWorkflow } from './object-workflow/workflow-model';

export const CONSTRUCTION_PROGRESS_CATEGORY_IDS = ['vykop', 'montaz', 'optika', 'ostatni'] as const;

export type ConstructionProgressCategoryId = (typeof CONSTRUCTION_PROGRESS_CATEGORY_IDS)[number];

export const CONSTRUCTION_PROGRESS_CATEGORY_LABELS: Record<ConstructionProgressCategoryId, string> = {
  vykop: 'Výkop',
  montaz: 'Montáž',
  /** Rozvaděč — montáž optiky (fáze + milníky provaření). */
  optika: 'Montáž optika',
  ostatni: 'Ostatní',
};

/** Typy objektů pro vážený průměr fází v dané kategorii hotovosti. */
export const CONSTRUCTION_CATEGORY_OBJECT_TYPES: Record<
  ConstructionProgressCategoryId,
  readonly string[]
> = {
  vykop: ['VYKOP', 'JAMA'],
  montaz: ['SPOJKOVISTE', 'BUDOVA'],
  optika: ['ROZVADEC'],
  /** Příprava/ostatní práce nemají mapování na fáze objektů — hotovost 0 %. */
  ostatni: [],
};

/** PPD / výkaz práce buckety → stejné 3 kategorie pro srovnání nákladů vs příjmů. */
export const CONSTRUCTION_CATEGORY_FINANCE_WORK_BUCKETS: Record<
  ConstructionProgressCategoryId,
  readonly FinanceWorkBucketId[]
> = {
  vykop: ['stavba'],
  montaz: ['montaz', 'zafuk'],
  optika: ['optika'],
  ostatni: ['priprava_ostatni'],
};

export interface ConstructionCategoryProgressInput {
  byObjectTypeAndPhaseCode: Readonly<Record<string, Readonly<Record<string, number>>>>;
}

function roundOneDecimal(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Vážený průměr průběhu fází objektů v kategorii (0–100). */
export function completionPercentForConstructionCategory(
  categoryId: ConstructionProgressCategoryId,
  input: ConstructionCategoryProgressInput,
): number {
  const types = CONSTRUCTION_CATEGORY_OBJECT_TYPES[categoryId];
  const byType = input.byObjectTypeAndPhaseCode ?? {};
  let sum = 0;
  let weight = 0;

  for (const objectType of types) {
    const byPhase = byType[objectType] ?? {};
    for (const [phaseCode, rawCount] of Object.entries(byPhase)) {
      const cnt = Number(rawCount ?? 0);
      if (!Number.isFinite(cnt) || cnt <= 0) continue;
      sum += getObjectPhaseProgressPercent(objectType, phaseCode) * cnt;
      weight += cnt;
    }
  }

  return weight > 0 ? roundOneDecimal(sum / weight) : 0;
}

/** Počet workflow objektů v kategorii (pro vážení celkové hotovosti). */
export function objectCountForConstructionCategory(
  categoryId: ConstructionProgressCategoryId,
  input: ConstructionCategoryProgressInput,
): number {
  const types = CONSTRUCTION_CATEGORY_OBJECT_TYPES[categoryId];
  const byType = input.byObjectTypeAndPhaseCode ?? {};
  let total = 0;
  for (const objectType of types) {
    const byPhase = byType[objectType] ?? {};
    for (const rawCount of Object.values(byPhase)) {
      const cnt = Number(rawCount ?? 0);
      if (Number.isFinite(cnt) && cnt > 0) total += cnt;
    }
  }
  return total;
}

export interface ConstructionCategoryProgressRow {
  id: ConstructionProgressCategoryId;
  label: string;
  completionPercent: number;
  objectCount: number;
}

/** Kategorie hotovosti + vážený průměr přes počty objektů. */
export function buildConstructionCategoryProgressRows(
  input: ConstructionCategoryProgressInput,
): ConstructionCategoryProgressRow[] {
  return CONSTRUCTION_PROGRESS_CATEGORY_IDS.map((id) => ({
    id,
    label: CONSTRUCTION_PROGRESS_CATEGORY_LABELS[id],
    completionPercent: completionPercentForConstructionCategory(id, input),
    objectCount: objectCountForConstructionCategory(id, input),
  }));
}

export function weightedOverallConstructionCompletion(
  rows: ReadonlyArray<ConstructionCategoryProgressRow>,
): number {
  let sum = 0;
  let weight = 0;
  for (const row of rows) {
    if (row.objectCount <= 0) continue;
    sum += row.completionPercent * row.objectCount;
    weight += row.objectCount;
  }
  return weight > 0 ? roundOneDecimal(sum / weight) : 0;
}

/** Typy objektů pro polygonovou hotovost «Výkop» (excavace). */
export const POLYGON_VYKOP_COMPLETION_OBJECT_TYPES = CONSTRUCTION_CATEGORY_OBJECT_TYPES.vykop;

const POLYGON_VYKOP_COMPLETION_TYPE_SET = new Set<string>(POLYGON_VYKOP_COMPLETION_OBJECT_TYPES);

export function isPolygonVykopCompletionObjectType(objectType: string): boolean {
  return POLYGON_VYKOP_COMPLETION_TYPE_SET.has(normalizeObjectTypeForLookup((objectType ?? '').trim()));
}

export type PolygonCompletionObjectInput = {
  type: string;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  /** BUDOVA: gates Zafouknutý as 100 % when digitální cesta absent. */
  digitalniCesta?: number | null;
};

export type PolygonCompletionSplit = {
  vykop: number;
  montaz: number;
};

function averagePhaseProgressForObjectFilter(
  objects: ReadonlyArray<PolygonCompletionObjectInput>,
  include: (normalizedType: string) => boolean,
): number {
  let sum = 0;
  let weight = 0;
  for (const o of objects) {
    const t = normalizeObjectTypeForLookup((o.type ?? '').trim());
    if (!t || !hasWorkflow(t) || !include(t)) continue;
    sum += getObjectPhaseProgressPercent(t, o.status ?? null, {
      metadata: o.metadata ?? null,
      ...(o.digitalniCesta !== undefined ? { digitalniCesta: o.digitalniCesta } : {}),
    });
    weight += 1;
  }
  return weight > 0 ? roundOneDecimal(sum / weight) : 0;
}

/** Vážený průměr fází výkopových objektů (VYKOP, JAMA) v polygonu. */
export function completionPercentForPolygonVykop(
  objects: ReadonlyArray<PolygonCompletionObjectInput>,
): number {
  return averagePhaseProgressForObjectFilter(objects, (t) => POLYGON_VYKOP_COMPLETION_TYPE_SET.has(t));
}

/** Vážený průměr fází všech ostatních workflow objektů v polygonu (montáž). */
export function completionPercentForPolygonMontaz(
  objects: ReadonlyArray<PolygonCompletionObjectInput>,
): number {
  return averagePhaseProgressForObjectFilter(objects, (t) => !POLYGON_VYKOP_COMPLETION_TYPE_SET.has(t));
}

export function splitPolygonCompletionPercent(
  objects: ReadonlyArray<PolygonCompletionObjectInput>,
): PolygonCompletionSplit {
  return {
    vykop: completionPercentForPolygonVykop(objects),
    montaz: completionPercentForPolygonMontaz(objects),
  };
}

/** Horní kategorie pod „Práce“ v tabulce finance (PPD buckety → zobrazení). */
export const FINANCE_WORK_TABLE_TOP_CATEGORY_IDS = ['stavba', 'montaz', 'optika', 'ostatni'] as const;

export type FinanceWorkTableTopCategoryId = (typeof FINANCE_WORK_TABLE_TOP_CATEGORY_IDS)[number];

/** @deprecated Alias pro kompatibilitu — používejte FINANCE_WORK_TABLE_TOP_CATEGORY_IDS. */
export const FINANCE_WORK_TABLE_CATEGORY_IDS = FINANCE_WORK_TABLE_TOP_CATEGORY_IDS;

/** @deprecated Alias — používejte FinanceWorkTableTopCategoryId. */
export type FinanceWorkTableCategoryId = FinanceWorkTableTopCategoryId;

export const FINANCE_WORK_TABLE_TOP_CATEGORY_LABELS: Record<FinanceWorkTableTopCategoryId, string> = {
  stavba: 'Stavba',
  montaz: 'Montáž',
  optika: 'Optika',
  ostatni: 'Ostatní',
};

/** @deprecated Alias — používejte FINANCE_WORK_TABLE_TOP_CATEGORY_LABELS. */
export const FINANCE_WORK_TABLE_CATEGORY_LABELS = FINANCE_WORK_TABLE_TOP_CATEGORY_LABELS;

/** Vnořené podkategorie pod Stavbou (výkop / pokládka / obnova / ostatní). */
export const FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_IDS = [
  'vykop',
  'pokladka',
  'obnova',
  'ostatni',
] as const;

export type FinanceWorkTableStavbaSubcategoryId =
  (typeof FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_IDS)[number];

export const FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_LABELS: Record<
  FinanceWorkTableStavbaSubcategoryId,
  string
> = {
  vykop: 'Výkop',
  /** Pokládka HDPE / multiduct (952602) — mimo čistý výkop. */
  pokladka: 'Pokládka',
  /** Obnova povrchu (položky s „obnova“ v názvu). */
  obnova: 'Obnova',
  /** Přesun materiálu, hodinová sazba, bourání, nad rámec bez obnovy, … */
  ostatni: 'Ostatní',
};

/** Vnořené podkategorie pod Montáží (montáž / záfuk). */
export const FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_IDS = ['montaz', 'zafuk'] as const;

export type FinanceWorkTableMontazSubcategoryId =
  (typeof FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_IDS)[number];

export const FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_LABELS: Record<
  FinanceWorkTableMontazSubcategoryId,
  string
> = {
  montaz: 'Montáž',
  zafuk: 'Záfuk',
};

export const FINANCE_WORK_TABLE_BUCKET_MAP: Record<
  FinanceWorkTableTopCategoryId,
  readonly FinanceWorkBucketId[]
> = {
  stavba: ['stavba'],
  montaz: ['montaz', 'zafuk'],
  optika: ['optika'],
  ostatni: ['priprava_ostatni'],
};

export function resolveFinanceWorkTableCategory(
  bucket: FinanceWorkBucketId,
): FinanceWorkTableTopCategoryId {
  if (bucket === 'stavba') return 'stavba';
  if (bucket === 'optika') return 'optika';
  if (bucket === 'priprava_ostatni') return 'ostatni';
  return 'montaz';
}

/** Výkaz práce / PPD název → horní kategorie tabulky (Stavba / Montáž / Optika / Ostatní). */
export function resolveFinanceWorkTableCategoryFromLine(
  activityName: string | null | undefined,
  role?: string | null,
  sap?: string | null,
): FinanceWorkTableTopCategoryId {
  const sapKey = sap?.trim();
  if (sapKey === FINANCE_MONTAZ_MULTIDUCT_SAP || sapKey === FINANCE_MONTAZ_ZAVRT_SAP) return 'montaz';
  if (isMontazZavrtPpdLine(sapKey, activityName)) return 'montaz';
  const bucket = resolveWorkReportFinanceBucket(activityName, role);
  return resolveFinanceWorkTableCategory(bucket);
}

/** Řádek stavby → podkategorie Výkop / Pokládka / Obnova / Ostatní. */
export function resolveFinanceWorkTableStavbaSubcategoryFromLine(
  activityName: string | null | undefined,
  sap?: string | null,
  unit?: string | null,
): FinanceWorkTableStavbaSubcategoryId {
  if (isFinanceStavbaPokladkaLine(activityName, sap)) return 'pokladka';
  if (isFinanceStavbaVykopLine(activityName, sap, unit)) return 'vykop';
  if (isFinanceStavbaObnovaLine(activityName)) return 'obnova';
  return 'ostatni';
}

/** Řádek montáže → podkategorie Montáž / Záfuk. */
export function resolveFinanceWorkTableMontazSubcategoryFromLine(
  activityName: string | null | undefined,
  role?: string | null,
): FinanceWorkTableMontazSubcategoryId {
  if (isZafukWorkName(activityName)) return 'zafuk';
  const bucket = resolveWorkReportFinanceBucket(activityName, role);
  return bucket === 'zafuk' ? 'zafuk' : 'montaz';
}
